import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Button } from 'reactstrap';
import {
  MessageSquare, Edit3, Plus, Trash2, CheckCircle, XCircle, Award,
  FileText, UserPlus, DollarSign, CreditCard,
} from 'react-feather';
import ComponentCard from '../../../layouts/containers/ComponentCard';
import matriculacionApiService from '../../../services/http/matriculacionApiService';
import logger from '../../../services/logger';

const ACTION_META = {
  crear: { label: 'Matrícula creada', icon: Plus, color: '#059669' },
  actualizarModulos: { label: 'Módulos actualizados', icon: Edit3, color: '#2563eb' },
  cambiarEstado: { label: 'Cambio de estado', icon: Edit3, color: '#f59e0b' },
  bajaMatricula: { label: 'Baja de matrícula', icon: XCircle, color: '#dc2626' },
  borrarRecibo: { label: 'Recibo eliminado', icon: Trash2, color: '#dc2626' },
  deleteRecibo: { label: 'Recibo eliminado', icon: Trash2, color: '#dc2626' },
  addRecibo: { label: 'Recibo añadido', icon: Plus, color: '#059669' },
  editRecibo: { label: 'Recibo editado', icon: DollarSign, color: '#2563eb' },
  confirmarPeticion: { label: 'Petición confirmada', icon: CheckCircle, color: '#059669' },
  crearConvocatoria: { label: 'Convocatoria creada', icon: Plus, color: '#059669' },
  eliminarConvocatoria: { label: 'Convocatoria eliminada', icon: Trash2, color: '#dc2626' },
  guardarEstadosActividad: { label: 'Estados de actividad', icon: Award, color: '#2563eb' },
  crearCuentaMoodle: { label: 'Cuenta Moodle', icon: UserPlus, color: '#6366f1' },
  crearCuentaD2L: { label: 'Cuenta D2L', icon: UserPlus, color: '#6366f1' },
  crearCuentaGoogle: { label: 'Cuenta Google', icon: UserPlus, color: '#6366f1' },
  crearCuentaOffice: { label: 'Cuenta Office', icon: UserPlus, color: '#6366f1' },
  comentario: { label: 'Comentario', icon: MessageSquare, color: '#6366f1' },
  enviarCambioTarjetaRedsys: {
    label: 'Enviar cambio tarjeta en Redsys',
    icon: CreditCard,
    color: '#2563eb',
  },
};

const RECIBO_FIELD_LABELS = {
  Concepto: 'Concepto',
  Neto: 'Neto',
  Bruto: 'Bruto',
  descuento: 'Descuento',
  Estado: 'Estado',
  Forma_de_pago: 'Forma de pago',
  idPago: 'Forma de pago',
  Fecha_Emision: 'Fecha emisión',
  Fecha_vencimiento: 'Fecha vencimiento',
  Fecha_cobro: 'Fecha cobro',
  Tipo_cliente: 'Tipo cliente',
  NUM_Recibo: 'Nº recibo',
  Num_factura: 'Nº factura',
  memo: 'Memo',
};

const RECIBO_FIELD_ORDER = [
  'Concepto', 'Neto', 'Bruto', 'descuento', 'Estado', 'Forma_de_pago',
  'idPago', 'Fecha_Emision', 'Fecha_vencimiento', 'Fecha_cobro', 'Tipo_cliente', 'memo',
];

const PAGE_SIZE = 50;

const FORMA_PAGO_KEYS = new Set([
  'idPago',
  'formaPago',
  'formaPagoId',
  'Forma_de_pago',
  'Forma_pago',
  'forma',
  'estadoPago',
]);
const ESTADO_RECIBO_KEYS = new Set([
  'Estado',
  'estado',
  'idEstado',
  'estadoRecibo',
  'estadoFactura',
  'estadoV',
]);

const traducirValor = (key, value, mapas) => {
  if (value == null) return value;
  const idNum = Number(value);
  if (Number.isNaN(idNum)) return value;
  if (FORMA_PAGO_KEYS.has(key) && mapas.metodosPago[idNum] != null) {
    return mapas.metodosPago[idNum];
  }
  if (ESTADO_RECIBO_KEYS.has(key) && mapas.estados[idNum] != null) {
    return mapas.estados[idNum];
  }
  return value;
};

const renderCambios = (cambios, mapas) => {
  if (!cambios || typeof cambios !== 'object') return null;

  const rows = [];
  Object.entries(cambios).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === 'object' && !Array.isArray(value)) {
      if ('antes' in value || 'despues' in value) {
        const antes = traducirValor(key, value.antes, mapas);
        const despues = traducirValor(key, value.despues, mapas);
        rows.push(
          <div key={key} style={{ fontSize: '0.75rem' }}>
            <strong>{key}:</strong>{' '}
            {value.antes != null && (
              <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>
                {String(antes)}
              </span>
            )}
            {value.antes != null && value.despues != null && ' → '}
            {value.despues != null && (
              <span style={{ color: '#059669' }}>{String(despues)}</span>
            )}
          </div>,
        );
        return;
      }
      if ('solicitado' in value || 'aplicado' in value) {
        const solicitado = traducirValor(key, value.solicitado, mapas);
        const aplicado = traducirValor(key, value.aplicado, mapas);
        rows.push(
          <div key={key} style={{ fontSize: '0.75rem' }}>
            <strong>{key}:</strong>{' '}
            <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>
              {String(solicitado)}
            </span>
            {' → '}
            <span style={{ color: '#059669' }}>{String(aplicado)}</span>
          </div>,
        );
        return;
      }
      rows.push(
        <div key={key} style={{ fontSize: '0.75rem' }}>
          <strong>{key}:</strong> {JSON.stringify(value)}
        </div>,
      );
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      const todosObjetos = value.every((v) => v && typeof v === 'object' && !Array.isArray(v));
      if (todosObjetos) {
        rows.push(
          <div key={key} style={{ fontSize: '0.75rem' }}>
            <strong>{key}:</strong>
            {value.map((item, idx) => {
              const claves = RECIBO_FIELD_ORDER.filter((k) => item[k] != null && item[k] !== '');
              const extras = Object.keys(item).filter(
                (k) => !RECIBO_FIELD_ORDER.includes(k) && item[k] != null && item[k] !== '' && k !== 'ccodcli' && k !== 'tipoCliente' && k !== 'NUM_Recibo' && k !== 'Num_factura',
              );
              const orden = [...claves, ...extras];
              return (
                <div
                  key={idx}
                  style={{
                    marginTop: '4px',
                    paddingLeft: '8px',
                    borderLeft: '2px solid #e5e7eb',
                  }}
                >
                  {orden.map((k) => {
                    const traducido = traducirValor(k, item[k], mapas);
                    const label = RECIBO_FIELD_LABELS[k] || k;
                    return (
                      <div key={k}>
                        <em style={{ color: '#6b7280' }}>{label}:</em> {String(traducido)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>,
        );
        return;
      }
      rows.push(
        <div key={key} style={{ fontSize: '0.75rem' }}>
          <strong>{key}:</strong> {value.join(', ')}
        </div>,
      );
      return;
    }
    rows.push(
      <div key={key} style={{ fontSize: '0.75rem' }}>
        <strong>{key}:</strong> {String(traducirValor(key, value, mapas))}
      </div>,
    );
  });

  if (rows.length === 0) return null;
  return (
    <div
      style={{
        marginTop: '6px',
        padding: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        color: '#374151',
      }}
    >
      {rows}
    </div>
  );
};

const HistorialMatricula = ({ headquarter, id, entidad = 'matricula' }) => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [metodosPagoMap, setMetodosPagoMap] = useState({});
  const [estadosMap, setEstadosMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    const cargarCatalogos = async () => {
      try {
        const [metodos, estados] = await Promise.all([
          matriculacionApiService.cargarMetodosPago({ headquarter }),
          matriculacionApiService.cargarEstadosRecibo({ headquarter }),
        ]);
        if (cancelled) return;
        const mp = {};
        (Array.isArray(metodos) ? metodos : []).forEach((m) => {
          mp[Number(m.id)] = m.descripcion;
        });
        const es = {};
        (Array.isArray(estados) ? estados : []).forEach((e) => {
          es[Number(e.id)] = e.descripcion;
        });
        setMetodosPagoMap(mp);
        setEstadosMap(es);
      } catch (error) {
        // Si falla, mantenemos mapas vacíos: el historial mostrará IDs como antes.
      }
    };
    cargarCatalogos();
    return () => {
      cancelled = true;
    };
  }, [headquarter]);

  const fetchPage = useCallback(
    async (currentOffset, append = false) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await matriculacionApiService.obtenerHistorial({
          entidad,
          entidadId: id,
          headquarter,
          limit: PAGE_SIZE,
          offset: currentOffset,
        });
        const items = Array.isArray(data) ? data : [];
        setEventos((prev) => (append ? [...prev, ...items] : items));
        setHasMore(items.length === PAGE_SIZE);
        setOffset(currentOffset + items.length);
      } catch (error) {
        logger.error('Error cargando historial:', error);
        if (!append) setEventos([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [entidad, id, headquarter],
  );

  useEffect(() => {
    fetchPage(0, false);
  }, [fetchPage]);

  if (loading) {
    return (
      <ComponentCard title="Historial de cambios" hasButton={false}>
        <div className="text-center">
          <Spinner size="sm" color="primary" /> Cargando historial...
        </div>
      </ComponentCard>
    );
  }

  return (
    <ComponentCard title="Historial de cambios" hasButton={false}>
      {eventos.length === 0 ? (
        <p className="text-muted text-center" style={{ fontSize: '0.85rem', margin: 0 }}>
          No hay cambios registrados todavía.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {eventos.map((ev) => {
            const meta = ACTION_META[ev.accion] || {
              label: ev.accion || 'Evento',
              icon: FileText,
              color: '#6b7280',
            };
            const Icon = meta.icon;
            return (
              <div
                key={ev.id}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: `${meta.color}20`,
                    color: meta.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: '4px',
                      gap: '8px',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>
                        {ev.nombre || ev.userEmail || 'Usuario desconocido'}
                      </span>
                      {ev.userEmail && ev.nombre && (
                        <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: '6px' }}>
                          ({ev.userEmail})
                        </span>
                      )}
                      {ev.origen === 'backfill' && (
                        <span
                          style={{
                            marginLeft: '6px',
                            padding: '1px 6px',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            borderRadius: '4px',
                          }}
                        >
                          histórico
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                      {ev.formattedDate}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: meta.color, fontWeight: 600, marginBottom: '4px' }}>
                    {meta.label}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {ev.descripcion}
                  </div>
                  {renderCambios(ev.cambios, { metodosPago: metodosPagoMap, estados: estadosMap })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasMore && (
        <div className="text-center mt-3">
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() => fetchPage(offset, true)}
            disabled={loadingMore}
          >
            {loadingMore ? <Spinner size="sm" /> : 'Cargar más'}
          </Button>
        </div>
      )}
    </ComponentCard>
  );
};

HistorialMatricula.propTypes = {
  headquarter: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  entidad: PropTypes.string,
};

export default HistorialMatricula;
